/**
 * ScheduleDetailModal.js - 일정 상세 모달
 * - 모든 참여자 → 목적지 경로선 (DirectionsService)
 * - 참여자 클릭 → 경로 포커스 + 지도 pan
 * - 나(isSelf) 경로의 단계별 안내 표시
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Clock, MapPin, Users, Navigation, ChevronDown, ChevronUp } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../config/apiConfig';


const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const PARTICIPANT_LABELS = 'ABCDEFGHIJKLMNOP';
const PARTICIPANT_COLORS = ['#EF4444', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#F97316', '#6366F1'];

// 도 레벨 광역 행정구역 → 도청 소재지 매핑 (서버 geocodingService와 동일)
// 도 이름만 입력 시 DirectionsService가 산악 지역 좌표로 geocoding → ZERO_RESULTS 방지
const PROVINCE_CAPITAL = {
  '강원도': '강원도 춘천',
  '강원특별자치도': '강원도 춘천',
  '경기도': '경기도 수원',
  '충청북도': '충청북도 청주',
  '충청남도': '충청남도 홍성',
  '전라북도': '전라북도 전주',
  '전북특별자치도': '전북특별자치도 전주',
  '전라남도': '전라남도 무안',
  '경상북도': '경상북도 안동',
  '경상남도': '경상남도 창원',
  '제주도': '제주특별자치도 제주',
  '제주특별자치도': '제주특별자치도 제주',
};

const ScheduleDetailModal = ({ isOpen, onClose, suggestion, memberLocations: externalLocations = [], roomId }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [destReady, setDestReady] = useState(false);   // geocode 완료 여부
  const [geocodeFailed, setGeocodeFailed] = useState(false); // geocode + Places 둘 다 실패
  const [focusedIdx, setFocusedIdx] = useState(null);
  const [fetchedLocations, setFetchedLocations] = useState([]); // 자체 fetch 결과
  const [routeDurations, setRouteDurations] = useState({});   // { idx: DirectionsResult }
  const [routeLoading, setRouteLoading] = useState({});       // { idx: bool }
  const [expandedSteps, setExpandedSteps] = useState(false);  // 단계별 안내 펼침

  // rawLocations: fetchedLocations 우선
  // fetchedLocations 없으면 SuggestionModal이 넘긴 externalLocations 폴백 (GPS 좌표 있음)
  const rawLocations = fetchedLocations.length > 0 ? fetchedLocations : externalLocations;

  // [5단계 수정] isRejected는 서버 room-members API가 suggestionId 기반으로 직접 반환
  // fetchedLocations에 이미 isRejected 필드가 포함되어 있음
  const memberLocations = rawLocations.map(p => ({
    ...p,
    isRejected: p.isRejected ?? false,
  }));

  console.log('[ScheduleDetailModal] memberLocations JSON:', JSON.stringify(memberLocations.map(l => ({ name: l.name, uid: l.userId?.slice(-6), isRejected: l.isRejected }))));

  const mapDivRef = useRef(null);
  const mapInitRef = useRef(false);          // 지도(목적지) 초기화 여부
  const participantsDrawnRef = useRef(false); // 참여자 경로 계산 완료 여부
  const mapMarkersDrawnRef = useRef(false);   // 참여자 마커/폴리라인 지도 표시 완료 여부
  const mapInstanceRef = useRef(null);
  const destLocationRef = useRef(null);      // geocode 결과 저장 (마커/폴리라인 그리기에서 재사용)
  const polylinesRef = useRef([]);
  const boundsRef = useRef(null);
  const membersRef = useRef([]);

  // Bug 4: 불참자 제외한 멤버만 지도/경로 계산에 사용
  const membersWithCoords = memberLocations.filter(p => !p.isRejected && p.lat && p.lng);
  // ★ membersRef는 전체 배열(memberLocations) 기준 — focusedIdx / routeDurations 인덱스와 일치
  membersRef.current = memberLocations;

  // Maps JS API 로드
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      // API 키 없음 → 즉시 HTTP fallback으로 전환 (기존 12초 대기 제거)
      setGeocodeFailed(true);
      return;
    }
    if (window.google?.maps) { setMapLoaded(true); return; }
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      let elapsed = 0;
      const check = setInterval(() => {
        if (window.google?.maps) { setMapLoaded(true); clearInterval(check); return; }
        elapsed += 100;
        if (elapsed >= 3000) { clearInterval(check); setGeocodeFailed(true); } // 3초 타임아웃 → iframe 폴백
      }, 100);
      return () => clearInterval(check);
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&language=ko&region=KR`;
    script.async = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setGeocodeFailed(true); // API 키/네트워크 오류 → iframe 폴백
    document.head.appendChild(script);
  }, []);

  // 모달 닫힐 때 초기화
  useEffect(() => {
    if (!isOpen) {
      mapInitRef.current = false;
      participantsDrawnRef.current = false;
      mapMarkersDrawnRef.current = false;
      // DirectionsRenderer는 setMap(null) 호출 후 배열 비워야 지도에서 제거됨
      polylinesRef.current.forEach(r => { if (r) try { r.setMap(null); } catch (_) {} });
      polylinesRef.current = [];
      mapInstanceRef.current = null;
      destLocationRef.current = null;
      boundsRef.current = null;
      setDestReady(false);
      setGeocodeFailed(false);
      setFocusedIdx(null);
      setRouteDurations({});
      setRouteLoading({});
      setExpandedSteps(false);
      setFetchedLocations([]);
    }
  }, [isOpen]);

  // 모달이 열린 채로 제안이 바뀌면(불참/재참석으로 새 제안 생성) 경로 재계산 강제
  useEffect(() => {
    if (!isOpen) return;
    participantsDrawnRef.current = false;
    polylinesRef.current.forEach(poly => { if (poly) try { poly.setMap(null); } catch (_) {} });
    polylinesRef.current = [];
    setRouteDurations({});
    setRouteLoading({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion?._id]);

  // roomId가 있을 때 멤버 위치 자체 fetch (불참자 제외는 클라이언트 측에서 처리)
  useEffect(() => {
    if (!isOpen || !roomId) return;
    const fetchLocations = async () => {
      try {
        // [3-B 수정] auth.currentUser가 null이면 최대 3초 대기 (Firebase 초기화 타이밍 이슈 대응)
        let user = auth.currentUser;
        if (!user) {
          user = await new Promise((resolve) => {
            const timeout = setTimeout(() => resolve(null), 3000);
            const unsub = auth.onAuthStateChanged((u) => {
              clearTimeout(timeout);
              unsub();
              resolve(u);
            });
          });
        }
        if (!user) return;
        const token = await user.getIdToken().catch(() => null);
        if (!token) return;

        const appointmentDate = suggestion?.date || null;

        // [5단계] suggestionId를 서버에 전달 → 서버가 isRejected 직접 반환
        const suggestionId = suggestion?._id || null;
        let queryStr = appointmentDate ? `?forDate=${appointmentDate}` : '';
        if (suggestionId) {
          const sep = queryStr ? '&' : '?';
          queryStr += `${sep}suggestionId=${suggestionId}`;
        }

        // GPS 항상 수집: 집주소 미등록 시 서버 fallback으로 사용 (미래 약속 포함)
        try {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const sep = queryStr ? '&' : '?';
                queryStr += `${sep}lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`;
                resolve();
              },
              () => resolve(),
              { maximumAge: 60000, timeout: 2000 }
            );
          });
        } catch (_) {}

        const fetchUrl = `${API_BASE_URL}/api/engine/room-members/${roomId}${queryStr}`;
        console.log('[ScheduleDetailModal] fetch URL:', fetchUrl, '| suggestionId:', suggestionId);
        const res = await fetch(fetchUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.locations)) {
          console.log('[ScheduleDetailModal] fetch 결과 JSON:', JSON.stringify(data.locations.map(l => ({ name: l.name, isRejected: l.isRejected, uid: l.userId?.slice(-6) }))));
          setFetchedLocations(data.locations);
        } else {
          console.warn('[ScheduleDetailModal] room-members 응답 이상:', data);
        }
      } catch (err) {
        console.warn('[ScheduleDetailModal] fetchLocations 실패:', err?.message);
      }
    };
    fetchLocations();
  // [3-B 수정] suggestion?._id 의존 추가: 같은 날짜여도 다른 제안으로 열리면 재fetch
  // [출발지변경] suggestion?.location 의존 추가: COMPLAINT_DEPARTURE가 같은 _id 안에서 location 변경 시 재fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roomId, suggestion?._id, suggestion?.location]);

  // ── Effect 1: 지도 + 목적지 마커 초기화 (1회) ──
  useEffect(() => {
    if (!isOpen || !mapLoaded || !suggestion?.location) return;
    if (mapInitRef.current) return;
    // el 없으면 mapInitRef true 설정 전에 return (DOM 준비 전 실행 방지)
    const el = mapDivRef.current;
    if (!el) return;
    mapInitRef.current = true;

    console.log('[ScheduleDetailModal] Effect1 시작 | location:', suggestion.location,
      '| locationLat:', suggestion.locationLat, '| locationLng:', suggestion.locationLng,
      '| mapLoaded:', mapLoaded);

    // geocoder 응답 없음 대비 8초 타임아웃 → iframe 폴백
    const geoTimeout = setTimeout(() => setGeocodeFailed(true), 8000);

    const initMap = (destLocation) => {
      clearTimeout(geoTimeout);
      destLocationRef.current = destLocation;
      console.log('[ScheduleDetailModal] initMap 호출:', destLocation);
      const map = new window.google.maps.Map(el, { center: destLocation, zoom: 13, language: 'ko', region: 'KR' });
      mapInstanceRef.current = map;
      new window.google.maps.Marker({ position: destLocation, map, title: suggestion.location, animation: window.google.maps.Animation.DROP });
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(destLocation);
      boundsRef.current = bounds;
      setDestReady(true); // Effect 2 트리거
    };

    // DB에 좌표가 저장된 경우 geocoding 완전 스킵 → 즉시 지도 표시
    if (suggestion?.locationLat && suggestion?.locationLng) {
      console.log('[ScheduleDetailModal] DB 좌표 사용 (geocoding 스킵):', suggestion.locationLat, suggestion.locationLng);
      initMap({ lat: suggestion.locationLat, lng: suggestion.locationLng });
      return () => clearTimeout(geoTimeout);
    }
    console.log('[ScheduleDetailModal] DB 좌표 없음 → geocoding 시작');

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: suggestion.location, region: 'KR' }, (results, status) => {
        if (status === 'OK' && results[0]) {
          initMap(results[0].geometry.location);
        } else {
          try {
            const service = new window.google.maps.places.PlacesService(document.createElement('div'));
            service.findPlaceFromQuery({
              query: suggestion.location,
              fields: ['geometry', 'name'],
              locationBias: { lat: 37.5665, lng: 126.978 },
            }, async (placeResults, placeStatus) => {
              clearTimeout(geoTimeout);
              if (placeStatus === 'OK' && placeResults[0]) {
                initMap(placeResults[0].geometry.location);
              } else {
                // 3차: 서버 geocode 프록시 (API 키 referrer 제한 우회)
                try {
                  const token = await auth.currentUser?.getIdToken();
                  const geoRes = await fetch(
                    `${API_BASE_URL}/api/engine/geocode?place=${encodeURIComponent(suggestion.location)}`,
                    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                  );
                  const geoData = await geoRes.json();
                  if (geoData.success && geoData.lat && geoData.lng) {
                    initMap({ lat: geoData.lat, lng: geoData.lng });
                  } else {
                    setGeocodeFailed(true); // 서버 geocode도 실패 → iframe 폴백
                  }
                } catch {
                  setGeocodeFailed(true);
                }
              }
            });
          } catch {
            clearTimeout(geoTimeout);
            setGeocodeFailed(true); // PlacesService 자체 오류 → iframe 폴백
          }
        }
      });
    } catch {
      clearTimeout(geoTimeout);
      setGeocodeFailed(true);
    }
    return () => clearTimeout(geoTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mapLoaded, suggestion?.location]);

  // ── Effect 2A: 멤버 도착 즉시 "계산 중..." 로딩 상태 표시 ──
  useEffect(() => {
    if (!isOpen || memberLocations.length === 0) return;
    const loading = {};
    memberLocations.forEach((p, i) => {
      if ((p.lat && p.lng) || p.address) loading[i] = true;
    });
    setRouteLoading(loading);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, memberLocations.length]);

  // ── Effect 2C: 12초 후에도 destReady 없으면 강제 geocodeFailed ──
  // Maps JS API가 아예 로드되지 않는 경우 Effect 1이 실행 안 되므로
  // geocodeFailed를 강제로 set해서 Effect 2B가 HTTP fallback으로 경로 계산하게 함
  useEffect(() => {
    if (!isOpen || destReady) return;
    const timer = setTimeout(() => setGeocodeFailed(true), 12000);
    return () => clearTimeout(timer);
  }, [isOpen, destReady]);

  // ── Effect 2B: 참여자 경로 계산 + 지도 마커/폴리라인 ──
  // - geocode 완료(destReady) 또는 실패(geocodeFailed) 후에만 실행
  // - Maps JS 로드됨 → DirectionsService + 마커/폴리라인
  // - Maps JS 없음(키 제한 등) → 서버 HTTP 프록시 경유 경로 계산
  useEffect(() => {
    if (!isOpen || !suggestion?.location) return;
    if (!destReady && !geocodeFailed) return;   // geocode 결과 대기
    if (participantsDrawnRef.current) return;    // 이미 계산 완료

    // Bug 4 수정: 불참자 제외 후 경로 계산
    const membersToCalc = memberLocations.filter(p => !p.isRejected && ((p.lat && p.lng) || p.address));
    if (membersToCalc.length === 0) return;

    participantsDrawnRef.current = true;

    const mapsJsAvailable = !!(window.google?.maps?.DirectionsService);
    const destForRoutes = destLocationRef.current || suggestion.location;
    const map = mapInstanceRef.current;
    const bounds = boundsRef.current;
    if (map && bounds) mapMarkersDrawnRef.current = true;

    // ── Maps JS 없을 때: 서버 HTTP 프록시로 경로 계산 ──
    const calcRouteHTTP = async (originStr, i) => {
      const destStr = destLocationRef.current
        ? (typeof destLocationRef.current.lat === 'function'
            ? `${destLocationRef.current.lat()},${destLocationRef.current.lng()}`
            : `${destLocationRef.current.lat},${destLocationRef.current.lng}`)
        : (suggestion?.locationLat && suggestion?.locationLng)
          ? `${suggestion.locationLat},${suggestion.locationLng}`
          : suggestion.location;
      try {
        let token = null;
        try { token = await auth.currentUser?.getIdToken(); } catch (_) {}
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        for (const mode of ['transit', 'driving', 'walking']) {
          const res = await fetch(
            `${API_BASE_URL}/api/engine/directions?origin=${encodeURIComponent(originStr)}&dest=${encodeURIComponent(destStr)}&mode=${mode}`,
            { headers }
          );
          const data = await res.json();
          if (data.status === 'OK' && data.routes?.length > 0) {
            setRouteDurations(prev => ({ ...prev, [i]: data }));
            break;
          }
        }
      } catch (_) {}
      setRouteLoading(prev => ({ ...prev, [i]: false }));
    };

    // ── Maps JS 있을 때: DirectionsService 사용 ──
    // fallbackOrigin: 좌표 실패 시 재시도할 문자열 출발지 (커스텀 출발지용)
    const calcRoute = (origin, color, i, fallbackOrigin = null) => {
      let done = false;
      const routeTimeout = setTimeout(() => {
        if (!done) { done = true; setRouteLoading(prev => ({ ...prev, [i]: false })); }
      }, 15000);
      try {
        const ds = new window.google.maps.DirectionsService();
        const drawResult = (result) => {
          if (done) return;
          done = true;
          clearTimeout(routeTimeout);
          const currentMap = mapInstanceRef.current;
          if (currentMap) {
            // DirectionsRenderer 사용: Google이 transit 경로를 직접 렌더링
            // overview_path/step.path의 희박(2점) 문제를 우회 → KTX/지하철도 실제 선로 표시
            const renderer = new window.google.maps.DirectionsRenderer({
              map: currentMap,
              directions: result,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: color,
                strokeWeight: 4,
                strokeOpacity: 0.85,
                zIndex: 1,
              },
            });
            polylinesRef.current[i] = renderer;
          }
          setRouteDurations(prev => ({ ...prev, [i]: result }));
          setRouteLoading(prev => ({ ...prev, [i]: false }));
        };
        const failRoute = () => {
          if (done) return;
          // 좌표 실패 + fallbackOrigin(라벨 문자열) 있으면 문자열로 재시도 (TRANSIT → DRIVING 순)
          // 도 레벨 광역 행정구역이면 도청 도시로 매핑 (예: "강원도" → "강원도 춘천")
          if (fallbackOrigin) {
            clearTimeout(routeTimeout); // timeout만 제거, done은 아직 false 유지 → drawResult 정상 동작
            const mappedOrigin = PROVINCE_CAPITAL[fallbackOrigin.trim()] || fallbackOrigin;
            try {
              const ds2 = new window.google.maps.DirectionsService();
              const depTime2 = new Date();
              ds2.route({
                origin: mappedOrigin, destination: destForRoutes,
                travelMode: window.google.maps.TravelMode.TRANSIT,
                region: 'KR', transitOptions: { departureTime: depTime2 },
              }, (r1, s1) => {
                if (s1 === 'OK') { drawResult(r1); return; }
                ds2.route({ origin: mappedOrigin, destination: destForRoutes, travelMode: window.google.maps.TravelMode.DRIVING, region: 'KR' },
                  (r2, s2) => {
                    if (s2 === 'OK') { drawResult(r2); return; }
                    done = true;
                    setRouteLoading(prev => ({ ...prev, [i]: false }));
                  }
                );
              });
            } catch (_) { done = true; setRouteLoading(prev => ({ ...prev, [i]: false })); }
            return;
          }
          done = true;
          clearTimeout(routeTimeout);
          setRouteLoading(prev => ({ ...prev, [i]: false }));
        };
        const depTime = new Date();
        ds.route({
          origin, destination: destForRoutes,
          travelMode: window.google.maps.TravelMode.TRANSIT,
          region: 'KR', language: 'ko',
          transitOptions: { departureTime: depTime },
        }, (r1, s1) => {
          if (s1 === 'OK') { drawResult(r1); return; }
          ds.route({ origin, destination: destForRoutes, travelMode: window.google.maps.TravelMode.DRIVING, region: 'KR' },
            (r2, s2) => {
              if (s2 === 'OK') { drawResult(r2); return; }
              ds.route({ origin, destination: destForRoutes, travelMode: window.google.maps.TravelMode.WALKING, region: 'KR' },
                (r3, s3) => { if (s3 === 'OK') drawResult(r3); else failRoute(); }
              );
            }
          );
        });
      } catch (_) {
        clearTimeout(routeTimeout);
        setRouteLoading(prev => ({ ...prev, [i]: false }));
      }
    };

    memberLocations.forEach((p, i) => {
      // Bug 4 수정: 불참자는 지도 마커/경로 생략
      if (p.isRejected) {
        setRouteLoading(prev => ({ ...prev, [i]: false }));
        return;
      }
      const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
      if (mapsJsAvailable) {
        // Maps JS 로드됨 → 마커 + DirectionsService
        const addMarkerAndRoute = (markerPos) => {
          if (map && bounds) {
            bounds.extend(markerPos);
            new window.google.maps.Marker({
              position: markerPos, map,
              title: `${p.name}${p.isSelf ? ' (나)' : ''}${p.departureLabel ? ` [${p.departureLabel} 출발]` : ''}`,
              label: { text: PARTICIPANT_LABELS[i] || String(i + 1), color: '#fff', fontWeight: 'bold', fontSize: '11px' },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: p.isSelf ? 16 : 13,
                fillColor: color, fillOpacity: 1,
                strokeColor: '#fff', strokeWeight: p.isSelf ? 3 : 2,
              },
            });
          }
          // 좌표로 경로 계산, 실패 시 departureLabel 문자열로 재시도
          // (서버 geocodingService가 광역 행정구역이면 도청 도시 좌표로 저장하므로 좌표 우선 사용)
          calcRoute(markerPos, color, i, p.departureLabel || null);
        };
        if (p.lat && p.lng) {
          addMarkerAndRoute({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) });
        } else if (p.address) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: p.address, region: 'KR' }, (results, status) => {
            if (status === 'OK' && results[0]) addMarkerAndRoute(results[0].geometry.location);
            else setRouteLoading(prev => ({ ...prev, [i]: false }));
          });
        }
      } else {
        // Maps JS 없음 → 서버 HTTP 프록시로 경로만 계산 (마커/폴리라인 없음)
        if (p.lat && p.lng) {
          // 좌표 기반 경로 계산 (geocodingService가 광역 행정구역이면 도청 도시 좌표로 저장함)
          // 실패 시 HTTP calcRouteHTTP 내부에서 transit/driving/walking 순차 시도
          calcRouteHTTP(`${parseFloat(p.lat)},${parseFloat(p.lng)}`, i);
        } else if (p.address) {
          calcRouteHTTP(p.address, i);
        }
      }
    });

    if (map && bounds && mapsJsAvailable) map.fitBounds(bounds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, destReady, geocodeFailed, memberLocations.length, suggestion?.location, suggestion?._id]);

  // 포커스 변경 → 경로 강조/흐리기
  useEffect(() => {
    const polylines = polylinesRef.current;
    polylines.forEach((poly, i) => {
      if (!poly) return;
      // polylinesRef에는 DirectionsRenderer가 저장됨 → polylineOptions 래퍼 필요
      if (focusedIdx === null) {
        poly.setOptions({ polylineOptions: { strokeWeight: 4, strokeOpacity: 0.85, zIndex: 1 } });
      } else {
        poly.setOptions({
          polylineOptions: {
            strokeWeight: i === focusedIdx ? 7 : 2,
            strokeOpacity: i === focusedIdx ? 1.0 : 0.18,
            zIndex: i === focusedIdx ? 10 : 1,
          },
        });
      }
    });

    if (focusedIdx !== null) {
      const p = membersRef.current[focusedIdx];
      if (p && mapInstanceRef.current) {
        mapInstanceRef.current.panTo({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) });
        mapInstanceRef.current.setZoom(14);
      }
    } else if (mapInstanceRef.current && boundsRef.current) {
      mapInstanceRef.current.fitBounds(boundsRef.current);
    }
  }, [focusedIdx]);

  if (!isOpen) return null;

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
  };
  const formatDistance = (meters) => `${(meters / 1000).toFixed(1)}km`;

  // 포커스된 참여자 (없으면 isSelf) 의 경로 결과
  const displayIdx = focusedIdx !== null ? focusedIdx : membersRef.current.findIndex(p => p.isSelf);
  const displayResult = displayIdx >= 0 ? routeDurations[displayIdx] : null;
  const displayMember = displayIdx >= 0 ? membersRef.current[displayIdx] : null;
  const displayRoute = displayResult?.routes?.[0];
  const displayLeg = displayRoute?.legs?.[0];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">일정 상세</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* 기본 정보 */}
          <div className="space-y-2">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{suggestion?.summary}</h3>
            <div className="space-y-2 text-sm sm:text-base text-gray-700">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-blue-500" />
                <span className="font-medium">{suggestion?.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-green-500" />
                <span className="font-medium">{suggestion?.startTime} ~ {suggestion?.endTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-red-500" />
                <span className="font-medium">{suggestion?.location || '미정'}</span>
              </div>
              {suggestion?.memberResponses && (
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-purple-500" />
                  <span className="font-medium">
                    참석자 {suggestion.memberResponses.filter(r => r.status === 'accepted').length}명
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 지도 */}
          {suggestion?.location && suggestion.location !== '미정' && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <MapPin size={15} /> 위치 및 경로
              </h4>

              {/* 지도: iframe을 즉시 표시, Maps JS 로드 완료 시 위에 오버레이 */}
              <div style={{ position: 'relative', height: 300, borderRadius: 8, overflow: 'hidden', border: '1px solid #D1D5DB' }}>
                {/* iframe: 항상 렌더링 (즉시 표시) */}
                <iframe
                  title="지도"
                  width="100%"
                  height="100%"
                  style={{ border: 0, position: 'absolute', top: 0, left: 0 }}
                  loading="eager"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(suggestion.location)}&hl=ko&output=embed`}
                />
                {/* Maps JS 인터랙티브 맵: 항상 DOM에 존재 (mapDivRef), destReady 시 iframe 위에 표시 */}
                {GOOGLE_MAPS_API_KEY && (
                  <div
                    ref={mapDivRef}
                    style={{
                      width: '100%', height: '100%',
                      position: 'absolute', top: 0, left: 0,
                      opacity: (destReady && !geocodeFailed) ? 1 : 0,
                      transition: 'opacity 0.4s',
                      pointerEvents: (destReady && !geocodeFailed) ? 'auto' : 'none',
                    }}
                  />
                )}
              </div>

              {/* 참여자 범례 — 클릭 → 포커스 */}
              {membersWithCoords.length > 0 && (
                <div className="space-y-1.5">
                  {memberLocations.map((p, i) => {
                    // Bug 4 수정: 불참자는 지도에 표시하지 않고 불참 배지만 표시
                    if (p.isRejected) {
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 10px',
                          background: '#FEF2F2',
                          borderRadius: 10,
                          border: '1px solid #FECACA',
                          opacity: 0.7,
                        }}>
                          <span style={{
                            background: '#D1D5DB', color: '#fff', borderRadius: '50%',
                            width: 26, height: 26, flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700,
                          }}>
                            {PARTICIPANT_LABELS[i]}
                          </span>
                          <span className="text-sm font-semibold text-gray-500 flex-1">
                            {p.name}
                            {p.isSelf && <span style={{ fontSize: 10, color: '#9CA3AF' }}> （나）</span>}
                          </span>
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            color: '#EF4444', background: '#FEE2E2',
                            borderRadius: 6, padding: '2px 8px',
                          }}>불참</span>
                        </div>
                      );
                    }

                    const hasCoords = !!(p.lat && p.lng);
                    const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
                    const isFocused = focusedIdx === i;
                    const result = routeDurations[i];
                    const loading = routeLoading[i];
                    const leg = result?.routes?.[0]?.legs?.[0];
                    const srcBadge = p.locationSource === 'gps'
                      ? <span className="text-[10px] text-green-600 bg-green-50 rounded px-1 ml-1">현재</span>
                      : p.locationSource === 'address'
                        ? <span className="text-[10px] text-gray-500 bg-gray-100 rounded px-1 ml-1">집</span>
                        : null;

                    return (
                      <div
                        key={i}
                        onClick={() => hasCoords && setFocusedIdx(prev => prev === i ? null : i)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 10px',
                          background: isFocused ? color + '20' : p.isSelf ? color + '10' : '#F9FAFB',
                          borderRadius: 10,
                          border: isFocused ? `2px solid ${color}` : p.isSelf ? `1.5px solid ${color}30` : '1px solid #F3F4F6',
                          cursor: hasCoords ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                        }}
                      >
                        <span style={{
                          background: hasCoords ? color : '#D1D5DB',
                          color: '#fff', borderRadius: '50%',
                          width: 26, height: 26, flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700,
                          boxShadow: isFocused ? `0 0 0 3px ${color}50` : 'none',
                          transform: isFocused ? 'scale(1.15)' : 'scale(1)',
                          transition: 'all 0.15s',
                        }}>
                          {PARTICIPANT_LABELS[i]}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 flex-1 flex items-center flex-wrap gap-1">
                          {p.name}
                          {p.isSelf && <span style={{ fontSize: 10, color }}>（나）</span>}
                          {srcBadge}
                        </span>
                        {hasCoords ? (
                          loading ? (
                            <span className="text-xs text-gray-400">계산 중...</span>
                          ) : leg ? (
                            <span style={{ color, fontWeight: 700, fontSize: 13 }}>
                              {leg.duration?.text}
                              <span className="text-gray-400 font-normal text-xs ml-1">({leg.distance?.text})</span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">경로 없음</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">위치 없음</span>
                        )}
                      </div>
                    );
                  })}

                  {focusedIdx !== null && (
                    <button
                      onClick={() => setFocusedIdx(null)}
                      className="w-full text-center text-xs text-gray-400 py-1 hover:text-gray-600 transition-colors"
                    >
                      전체 보기
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 선택된 참여자 경로 단계별 안내 */}
          {displayLeg && displayMember && (
            <div className="space-y-2">
              <button
                onClick={() => setExpandedSteps(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm font-semibold text-blue-700"
              >
                <span className="flex items-center gap-2">
                  <Navigation size={15} />
                  {displayMember.name}{displayMember.isSelf ? '（나）' : ''} 경로 안내
                  <span className="font-normal text-blue-500">
                    {displayLeg.duration?.text} · {displayLeg.distance?.text}
                  </span>
                </span>
                {expandedSteps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {expandedSteps && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  {displayLeg.steps?.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div dangerouslySetInnerHTML={{ __html: step.instructions }} className="text-gray-700" />
                        {step.transit && (
                          <div className="mt-1 flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded font-medium">
                              {step.transit.line.vehicle.name}
                            </span>
                            <span className="text-xs text-gray-600">{step.transit.line.short_name || step.transit.line.name}</span>
                            <span className="text-xs text-gray-400">({step.transit.num_stops}정거장)</span>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDuration(step.duration.value)} · {formatDistance(step.distance.value)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 text-center pt-1">* 실제 교통 상황에 따라 다를 수 있습니다.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDetailModal;
